import { useEffect, useState } from 'react';
import { default as Box } from '@mui/material/Box';
import { default as Switch } from '@mui/material/Switch';
import { ContentCard } from 'components/Card';
import { AlertDialog } from 'components/dialog';
import { InfoCard, WarningCard } from 'components/notification-cards';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';
import {
  defaultPreferencesDisabled,
  defaultPreferencesEnabled
} from '../constants';
import { useBraze } from '../hooks';
import { usePreferencesMutation, usePreferencesQuery } from '../model/query';

export const NotificationSettings = () => {
  const [isJobNotificationAlertOpen, setIsJobNotificationAlertOpen] =
    useState<boolean>(false);

  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    () => ('Notification' in window ? Notification.permission : 'default')
  );

  const { data, isLoading } = usePreferencesQuery();

  const [jobPushField, setJobPushField] = useState<boolean>(true);
  const [marketingPushField, setMarketingPushField] = useState<boolean>(true);
  const [marketingEmailField, setMarketingEmailField] = useState<boolean>(true);

  const braze = useBraze();
  const preferencesMutation = usePreferencesMutation();

  const isGranted = pushPermission === 'granted';
  const isDenied = pushPermission === 'denied';

  const refId = data?.data.notifications.refId || '';

  const updateDefaultPreferences = () => {
    const permission =
      'Notification' in window ? Notification.permission : null;

    if (permission === 'granted') {
      setPushPermission('granted');
      preferencesMutation.mutate({
        ...defaultPreferencesEnabled,
        pushPermission: true,
        refId
      });
    } else if (permission === 'denied') {
      setPushPermission('denied');
      preferencesMutation.mutate({
        ...defaultPreferencesDisabled,
        pushPermission: false,
        refId
      });
    }
  };

  const updatePreferences = ({
    jobPush = jobPushField,
    marketingPush = marketingPushField,
    marketingEmail = marketingEmailField
  } = {}) => {
    preferencesMutation.mutate({
      jobs: {
        email: true,
        push: jobPush
      },
      marketing: {
        email: marketingEmail,
        push: marketingPush
      },
      pushPermission: isGranted,
      refId
    });
  };

  const getPermission = () => {
    if (!braze) return;

    braze.requestPushPermission(
      // allowed
      () => {
        updateDefaultPreferences();
      },
      // denied or error
      () => {
        updateDefaultPreferences();
      }
    );
  };

  useEffect(() => {
    if (!data) return;
    setJobPushField(data.data?.notifications.jobs.push || false);
    setMarketingPushField(data.data?.notifications.marketing.push || false);
    setMarketingEmailField(data.data?.notifications.marketing.email || false);
  }, [data, isLoading]);

  return (
    <Box mt={8}>
      <ContentCard>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text variant="h5">
            {getTrans('notificationSettingsPage.pushTitle')}
          </Text>
          <Switch
            sx={{ mr: '-9px' }}
            value="pushPermission"
            checked={isGranted}
            disabled={isGranted || isDenied}
            onClick={getPermission}
          />
        </Box>
        {(isGranted || isDenied) && (
          <InfoCard sx={{ mt: 2 }}>
            <Text variant="body2">
              {getTrans('notificationSettingsPage.pushInfo')}
            </Text>
          </InfoCard>
        )}
      </ContentCard>
      <ContentCard>
        <Text variant="h5" mb={2}>
          {getTrans('notificationSettingsPage.jobNotifications.title')}
        </Text>
        <Text variant="body2">
          {getTrans('notificationSettingsPage.jobNotifications.subtitle')}
        </Text>
        <Box mt={2} display="flex" justifyContent="space-between">
          <Text variant="h5">
            {getTrans('notificationSettingsPage.pushLabel')}
          </Text>
          <Switch
            sx={{ mr: '-9px' }}
            value="jobPush"
            checked={jobPushField}
            onClick={() => {
              if (jobPushField) {
                setIsJobNotificationAlertOpen(true);
              } else {
                const newValue = !jobPushField;
                setJobPushField(newValue);
                updatePreferences({
                  jobPush: newValue
                });
              }
            }}
          />
        </Box>
        {isGranted && !jobPushField && (
          <WarningCard sx={{ mt: 2 }}>
            <Text variant="body1">
              {getTrans(
                'notificationSettingsPage.jobNotifications.warning.header'
              )}
            </Text>
            <Text variant="body2">
              {getTrans('notificationSettingsPage.jobNotifications.warning.p1')}
            </Text>
            <Text variant="body2">
              {getTrans('notificationSettingsPage.jobNotifications.warning.p2')}
            </Text>
          </WarningCard>
        )}
        <Box mt={2} display="flex" justifyContent="space-between">
          <Text variant="h5">
            {getTrans('notificationSettingsPage.emailLabel')}
          </Text>
        </Box>
        <InfoCard sx={{ mt: 2 }}>
          <Text variant="body1">
            {getTrans(
              'notificationSettingsPage.jobNotifications.emailInfo.header'
            )}
          </Text>
          <Text variant="body2">
            {getTrans('notificationSettingsPage.jobNotifications.emailInfo.p1')}
          </Text>
        </InfoCard>
      </ContentCard>
      <ContentCard>
        <Text variant="h5" mb={2}>
          {getTrans('notificationSettingsPage.marketingNotifications.title')}
        </Text>
        <Text variant="body2">
          {getTrans('notificationSettingsPage.marketingNotifications.subtitle')}
        </Text>
        <Box mt={2} display="flex" justifyContent="space-between">
          <Text variant="h5">
            {getTrans('notificationSettingsPage.pushLabel')}
          </Text>
          <Switch
            sx={{ mr: '-9px' }}
            value="marketingPush"
            checked={marketingPushField}
            onClick={() => {
              const newValue = !marketingPushField;
              setMarketingPushField(newValue);
              updatePreferences({
                marketingPush: newValue
              });
            }}
          />
        </Box>
        <Box mt={2} display="flex" justifyContent="space-between">
          <Text variant="h5">
            {getTrans('notificationSettingsPage.emailLabel')}
          </Text>
          <Switch
            sx={{ mr: '-9px' }}
            value="marketingEmail"
            checked={marketingEmailField}
            onClick={() => {
              const newValue = !marketingEmailField;
              setMarketingEmailField(newValue);
              updatePreferences({
                marketingEmail: newValue
              });
            }}
          />
        </Box>
      </ContentCard>
      <AlertDialog
        confirmColor="error"
        isOpen={isJobNotificationAlertOpen}
        title={getTrans(
          'notificationSettingsPage.jobNotifications.warning.title'
        )}
        confirmLabel={getTrans(
          'notificationSettingsPage.jobNotifications.warning.accept'
        )}
        cancelLabel={getTrans(
          'notificationSettingsPage.jobNotifications.warning.cancel'
        )}
        onClose={() => setIsJobNotificationAlertOpen(false)}
        onConfirm={() => {
          setJobPushField(false);
          updatePreferences({ jobPush: false });
          setIsJobNotificationAlertOpen(false);
        }}
      >
        <Box sx={{ pl: 4, pr: 4 }}>
          <Text variant="body2" textAlign="center">
            {getTrans(
              'notificationSettingsPage.jobNotifications.warning.subtitle'
            )}
          </Text>
        </Box>
      </AlertDialog>
    </Box>
  );
};
